import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useVideoToImagesStore from '../store/useVideoToImagesStore';
import {
  cancelDatasetBuildAPI,
  cancelVideoJobAPI,
  createModelAPI,
  createProjectAPI,
  getDatasetBuildsAPI,
  getUnlabeledCountAPI,
  deleteDatasetAPI,
  deleteDatasetItemsAPI,
  downloadDatasetAPI,
  getDatasetItemsAPI,
  getDatasetsAPI,
  getEngineInfoAPI,
  getModelVersionsAPI,
  getModelsAPI,
  getProjectsDropdownAPI,
  getVideoJobAPI,
  getVideoJobLogsAPI,
  getVideoJobsAPI,
  startDatasetBuildAPI,
  startExtractionAPI,
  uploadVideoSourcesAPI,
} from '../api/videoToImagesApi';
import { useSwal } from '../../../../components/Notifications/swal/SwalCom';
import { getLoggedInOwnerId } from '../../../../utils/auth';
import { useI18n } from '../../../../i18n';

/** Statuses that mean the backend is still working — drives the fast poll. */
export const ACTIVE_STATUSES = ['queued', 'inprogress'];

/** How many dataset images to pull per page. */
export const DATASET_PAGE_SIZE = 120;

/** Where a finished dataset opens for labeling — same map the model pages use. */
const LABELING_TARGET = {
  classification: '/ClassificationLabel',
  bounding_box: '/DetectionLabel',
  segmentation: '/SegmentationLabel',
};

export const isJobActive = (job) =>
  ACTIVE_STATUSES.includes(String(job?.status || '').toLowerCase());

export const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let scaled = value / 1024;
  let unit = 0;
  while (scaled >= 1024 && unit < units.length - 1) {
    scaled /= 1024;
    unit += 1;
  }
  return `${scaled.toFixed(scaled < 10 ? 1 : 0)} ${units[unit]}`;
};

const useVideoToImagesHook = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { swalFail, swalSuccess, swalConfirm } = useSwal();

  const {
    engine, supportedExtensions, setEngineInfo,
    targetMode, setTargetMode, datasetName, setDatasetName,
    projects, setProjects, models, setModels,
    selectedProjectId, setSelectedProjectId, selectedModelId, setSelectedModelId,
    selectedFiles, addSelectedFiles, removeSelectedFile, clearSelectedFiles,
    dragOver, setDragOver,
    isUploading, uploadPercent, uploadedBytes, setUploadState,
    stagedBatch, setStagedBatch,
    options, setOption, resetOptions,
    jobs, setJobs, jobsError, setJobsError,
    isStarting, setIsStarting,
    detailJob, setDetailJob, detailLogs, setDetailLogs,
    datasets, setDatasets, datasetsError, setDatasetsError,
    activeDataset, setActiveDataset,
    datasetItems, setDatasetItems, appendDatasetItems,
    datasetTotal, setDatasetTotal, isDatasetLoading, setIsDatasetLoading,
    selectedItemIds, toggleItemSelection, setSelectedItemIds, clearItemSelection,
    creatingProject, setCreatingProject, newProjectName, setNewProjectName,
    creatingModel, setCreatingModel, newModelName, setNewModelName,
    newModelType, setNewModelType, isCreatingTarget, setIsCreatingTarget,
    isDownloading, setIsDownloading,
    isBuildOpen, setIsBuildOpen,
    versionTarget, setVersionTarget,
    versionUnlabeled, setVersionUnlabeled,
    buildTask, setBuildTask,
    buildClassMode, setBuildClassMode,
    buildClassCount, setBuildClassCount,
    buildClassNames, setBuildClassNames,
    buildObjects, setBuildObjects,
    buildConfidence, setBuildConfidence,
    isStartingBuild, setIsStartingBuild,
    builds, setBuilds,
    transferProjectId, setTransferProjectId,
    transferModelId, setTransferModelId,
    transferVersionId, setTransferVersionId,
    transferModels, setTransferModels,
    modelVersions, setModelVersions,
    resetAll,
  } = useVideoToImagesStore();

  const resolveOwnerId = () => {
    const ownerId = getLoggedInOwnerId();
    if (!ownerId) console.warn('Owner ID not found in storage.');
    return ownerId;
  };

  // ======================================================================
  // LOOKUPS
  // ======================================================================

  const handleGetEngineInfo = useCallback(async () => {
    try {
      const response = await getEngineInfoAPI();
      setEngineInfo(response?.data?.data);
    } catch (error) {
      console.error('Error fetching extraction engine info:', error);
    }
  }, [setEngineInfo]);

  const handleGetProjects = useCallback(async () => {
    try {
      const ownerId = resolveOwnerId();
      if (!ownerId) return;
      const response = await getProjectsDropdownAPI({ owner_id: ownerId });
      setProjects(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    }
  }, [setProjects]);

  const handleGetModels = useCallback(async (projectId) => {
    if (!projectId) {
      setModels([]);
      return;
    }
    try {
      const ownerId = resolveOwnerId();
      if (!ownerId) return;
      const response = await getModelsAPI({ owner_id: ownerId, project_id: projectId });
      // get_models returns projects → models; flatten to the one project asked for.
      const flattened = (Array.isArray(response?.data) ? response.data : [])
        .flatMap((project) => project?.models || []);
      setModels(flattened);
    } catch (error) {
      console.error('Error fetching models:', error);
      setModels([]);
    }
  }, [setModels]);

  const handleSelectProject = useCallback(async (projectId) => {
    setSelectedProjectId(projectId);
    await handleGetModels(projectId);
  }, [setSelectedProjectId, handleGetModels]);

  // ======================================================================
  // FILE SELECTION
  // ======================================================================

  const isSupportedName = useCallback((name) => {
    const lower = String(name || '').toLowerCase();
    if (lower.endsWith('.zip')) return true;   // opened server-side
    if (!supportedExtensions.length) return true; // engine info not in yet — let the server decide
    return supportedExtensions.some((ext) => lower.endsWith(ext));
  }, [supportedExtensions]);

  /**
   * Normalize anything the browser hands us (file input, folder input, drop)
   * into `{ file, relativePath }`. The relative path is what gets sent as the
   * filename so the server can rebuild a folder tree.
   */
  const normalizeFiles = useCallback((fileList) => {
    const entries = Array.from(fileList || []).map((file) => ({
      file,
      relativePath: file.webkitRelativePath || file.name,
    }));
    // Client-side screen is a convenience only; the server re-checks the
    // extension AND the magic bytes and reports whatever it skipped.
    return entries.filter((entry) => isSupportedName(entry.relativePath));
  }, [isSupportedName]);

  const handleFilesPicked = useCallback((fileList) => {
    const normalized = normalizeFiles(fileList);
    if (normalized.length) {
      addSelectedFiles(normalized);
      setStagedBatch(null); // options/selection changed — re-upload before starting
    }
  }, [normalizeFiles, addSelectedFiles, setStagedBatch]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setDragOver(true);
  }, [setDragOver]);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setDragOver(false);
  }, [setDragOver]);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setDragOver(false);
    handleFilesPicked(event.dataTransfer?.files);
  }, [setDragOver, handleFilesPicked]);

  const handleRemoveFile = useCallback((relativePath) => {
    removeSelectedFile(relativePath);
    setStagedBatch(null);
  }, [removeSelectedFile, setStagedBatch]);

  const handleClearFiles = useCallback(() => {
    clearSelectedFiles();
    setStagedBatch(null);
  }, [clearSelectedFiles, setStagedBatch]);

  // ======================================================================
  // JOBS
  // ======================================================================

  const handleGetJobs = useCallback(async () => {
    try {
      const ownerId = resolveOwnerId();
      if (!ownerId) return;
      const response = await getVideoJobsAPI({ owner_id: ownerId, limit: 50 });
      setJobs(Array.isArray(response?.data?.data) ? response.data.data : []);
      setJobsError(null);
    } catch (error) {
      console.error('Error fetching video jobs:', error);
      setJobsError(t('videoToImages.jobs.loadFailed'));
    }
  }, [setJobs, setJobsError, t]);

  // ======================================================================
  // DATASETS (the holding area)
  // ======================================================================

  const handleGetDatasets = useCallback(async () => {
    try {
      const ownerId = resolveOwnerId();
      if (!ownerId) return;
      const response = await getDatasetsAPI({ owner_id: ownerId, limit: 100 });
      setDatasets(Array.isArray(response?.data?.data) ? response.data.data : []);
      setDatasetsError(null);
    } catch (error) {
      console.error('Error fetching datasets:', error);
      setDatasetsError(t('videoToImages.datasets.loadFailed'));
    }
  }, [setDatasets, setDatasetsError, t]);

  const handleOpenDataset = useCallback(async (datasetId) => {
    setIsDatasetLoading(true);
    clearItemSelection();
    try {
      const response = await getDatasetItemsAPI({
        dataset_id: datasetId, skip: 0, limit: DATASET_PAGE_SIZE,
      });
      const payload = response?.data?.data || {};
      setActiveDataset(payload.dataset || null);
      setDatasetItems(payload.items || []);
      setDatasetTotal(Number(payload.total || 0));
      await handleGetBuilds(datasetId);
    } catch (error) {
      console.error('Error opening dataset:', error);
      swalFail(t('videoToImages.datasets.heading'), t('videoToImages.datasets.loadFailed'));
    } finally {
      setIsDatasetLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  /** Next page of images, appended to what is already shown. */
  const handleLoadMoreItems = useCallback(async () => {
    if (!activeDataset?._id) return;
    setIsDatasetLoading(true);
    try {
      const response = await getDatasetItemsAPI({
        dataset_id: activeDataset._id,
        skip: datasetItems.length,
        limit: DATASET_PAGE_SIZE,
      });
      const payload = response?.data?.data || {};
      appendDatasetItems(payload.items || []);
      setDatasetTotal(Number(payload.total || 0));
    } catch (error) {
      console.error('Error loading more dataset images:', error);
    } finally {
      setIsDatasetLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDataset, datasetItems.length]);

  const handleCloseDataset = useCallback(() => {
    setActiveDataset(null);
    setDatasetItems([]);
    setDatasetTotal(0);
    clearItemSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAllItems = useCallback(() => {
    setSelectedItemIds(datasetItems.map((item) => item._id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetItems]);

  const handleRemoveSelected = useCallback(() => {
    if (!activeDataset?._id || !selectedItemIds.size) {
      swalFail(t('videoToImages.datasets.heading'), t('videoToImages.datasets.noneSelected'));
      return;
    }
    const ids = Array.from(selectedItemIds);
    swalConfirm(
      t('videoToImages.datasets.removeConfirmTitle'),
      t('videoToImages.datasets.removeConfirmText'),
      async (result) => {
        if (!result?.isConfirmed) return;
        try {
          await deleteDatasetItemsAPI({
            dataset_id: activeDataset._id, item_ids: JSON.stringify(ids),
          });
          await handleOpenDataset(activeDataset._id);
          await handleGetDatasets();
          swalSuccess(
            t('videoToImages.datasets.heading'),
            t('videoToImages.datasets.removed', { count: ids.length })
          );
        } catch (error) {
          console.error('Error removing dataset images:', error);
          swalFail(t('videoToImages.datasets.heading'), t('videoToImages.datasets.removeFailed'));
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDataset, selectedItemIds, handleOpenDataset, handleGetDatasets, t]);

  const handleDeleteDataset = useCallback((datasetId) => {
    swalConfirm(
      t('videoToImages.datasets.deleteDatasetConfirmTitle'),
      t('videoToImages.datasets.deleteDatasetConfirmText'),
      async (result) => {
        if (!result?.isConfirmed) return;
        try {
          await deleteDatasetAPI({ dataset_id: datasetId });
          handleCloseDataset();
          await handleGetDatasets();
          swalSuccess(
            t('videoToImages.datasets.heading'),
            t('videoToImages.datasets.datasetDeleted')
          );
        } catch (error) {
          console.error('Error deleting dataset:', error);
          swalFail(t('videoToImages.datasets.heading'), t('videoToImages.datasets.loadFailed'));
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleCloseDataset, handleGetDatasets, t]);

  /**
   * Download the dataset's images as a zip — the whole thing, or just the
   * selection.
   *
   * The server answers with the archive as the response body, so an error
   * arrives as a Blob too and has to be read back as text before it can be
   * shown; otherwise the user gets "[object Blob]" as the reason.
   */
  const handleDownloadDataset = useCallback(async (scope = 'all') => {
    if (!activeDataset?._id) return;
    const ids = scope === 'selected' ? Array.from(selectedItemIds) : [];
    if (scope === 'selected' && !ids.length) {
      swalFail(t('videoToImages.datasets.downloadFailed'), t('videoToImages.datasets.noneSelected'));
      return;
    }

    setIsDownloading(true);
    try {
      const params = { dataset_id: activeDataset._id };
      if (ids.length) params.item_ids = JSON.stringify(ids);
      const response = await downloadDatasetAPI(params);

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(activeDataset.name || 'dataset').replace(/[^\w .-]+/g, '_')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const count = Number(response?.headers?.['x-image-count'])
        || ids.length || activeDataset.itemCount || 0;
      swalSuccess(t('videoToImages.datasets.heading'),
        t('videoToImages.datasets.downloaded', { count }));
    } catch (error) {
      console.error('Error downloading the dataset:', error);
      let detail = error?.message || '';
      const body = error?.response?.data;
      if (body instanceof Blob) {
        try {
          detail = (JSON.parse(await body.text()).detail) || detail;
        } catch (parseError) {
          // Not JSON — keep the axios message.
        }
      } else if (body?.detail) {
        detail = body.detail;
      }
      swalFail(t('videoToImages.datasets.downloadFailed'), detail);
    } finally {
      setIsDownloading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDataset, selectedItemIds, t]);

  // ======================================================================
  // CREATING A DESTINATION INLINE
  // ======================================================================

  /**
   * Make a project without leaving the page.
   *
   * `save_project` answers HTTP **200 with `data: null`** when the name is
   * taken, so this branches on `message` — a status check would hand `null`
   * to the next call and fail three steps later with no useful reason.
   */
  const handleCreateProject = useCallback(async () => {
    const name = (newProjectName || '').trim();
    if (!name) {
      swalFail(t('videoToImages.target.createFailed'), t('videoToImages.target.nameRequired'));
      return null;
    }
    const ownerId = resolveOwnerId();
    if (!ownerId) return null;

    setIsCreatingTarget(true);
    try {
      const form = new FormData();
      form.append('name', name);
      form.append('detail', '');
      form.append('owner_id', ownerId);
      const response = await createProjectAPI(form);
      const body = response?.data || {};
      if (body.message !== 'success' || !body.data) {
        swalFail(t('videoToImages.target.createFailed'), body.message || '');
        return null;
      }
      const projectId = String(body.data);
      await handleGetProjects();
      setCreatingProject(false);
      await handleSelectProject(projectId);
      swalSuccess(t('videoToImages.title'), t('videoToImages.target.created', { name }));
      return projectId;
    } catch (error) {
      console.error('Error creating project:', error);
      swalFail(t('videoToImages.target.createFailed'),
        error?.response?.data?.detail || error?.message || '');
      return null;
    } finally {
      setIsCreatingTarget(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newProjectName, handleGetProjects, handleSelectProject, t]);

  /** Same contract as the project, except the new id arrives at `data._id`. */
  const handleCreateModel = useCallback(async () => {
    const name = (newModelName || '').trim();
    if (!name) {
      swalFail(t('videoToImages.target.createFailed'), t('videoToImages.target.nameRequired'));
      return null;
    }
    if (!selectedProjectId) {
      swalFail(t('videoToImages.target.createFailed'), t('videoToImages.target.missing'));
      return null;
    }
    const ownerId = resolveOwnerId();
    if (!ownerId) return null;

    setIsCreatingTarget(true);
    try {
      const form = new FormData();
      form.append('name', name);
      form.append('detail', '');
      form.append('owner_id', ownerId);
      form.append('model_type', newModelType);
      form.append('project_id', selectedProjectId);
      const response = await createModelAPI(form);
      const body = response?.data || {};
      if (body.message !== 'success' || !body.data || !body.data._id) {
        swalFail(t('videoToImages.target.createFailed'), body.message || '');
        return null;
      }
      const modelId = String(body.data._id);
      await handleGetModels(selectedProjectId);
      setCreatingModel(false);
      setSelectedModelId(modelId);
      swalSuccess(t('videoToImages.title'), t('videoToImages.target.created', { name }));
      return modelId;
    } catch (error) {
      console.error('Error creating model:', error);
      swalFail(t('videoToImages.target.createFailed'),
        error?.response?.data?.detail || error?.message || '');
      return null;
    } finally {
      setIsCreatingTarget(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newModelName, newModelType, selectedProjectId, handleGetModels, t]);

  // ======================================================================
  // PREPARING A DATASET FOR LABELING (the AI build)
  // ======================================================================

  const handleGetBuilds = useCallback(async (datasetId) => {
    try {
      const ownerId = resolveOwnerId();
      if (!ownerId) return;
      const response = await getDatasetBuildsAPI({
        owner_id: ownerId, dataset_id: datasetId || undefined, limit: 20,
      });
      setBuilds(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Error fetching dataset builds:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenBuild = useCallback(async () => {
    if (!selectedItemIds.size) {
      swalFail(t('videoToImages.build.heading'), t('videoToImages.datasets.noneSelected'));
      return;
    }
    setTransferProjectId('');
    setTransferModels([]);
    setModelVersions([]);
    setCreatingProject(false);
    setCreatingModel(false);
    setVersionTarget(null);
    setVersionUnlabeled(null);
    setIsBuildOpen(true);
    if (!projects.length) await handleGetProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemIds, projects.length, handleGetProjects, t]);

  /**
   * Label a model version's own images, for frames that went straight into a
   * model instead of a dataset.
   *
   * The unlabeled count is fetched on open so the dialog can say what it is
   * about to do — and refuse cleanly when there is nothing left to label,
   * rather than offering a button whose only outcome is an error.
   */
  const handleOpenVersionBuild = useCallback(async (job) => {
    if (!job?.modelDataInfoId || !job?.modelType) return;
    const target = {
      modelDataInfoId: job.modelDataInfoId,
      modelId: job.modelId,
      modelName: job.modelName,
      modelType: job.modelType,
      projectId: job.projectId,
      version: job.version,
    };
    setVersionTarget(target);
    setVersionUnlabeled(null);
    // An AI task names the model's own kind, so the task follows the model.
    setBuildTask(job.modelType === 'bounding_box' ? 'bounding_box' : 'classification');
    setIsBuildOpen(true);
    try {
      const response = await getUnlabeledCountAPI({
        model_data_info_id: job.modelDataInfoId, model_type: job.modelType,
      });
      setVersionUnlabeled(Number(response?.data?.data?.unlabeled ?? 0));
    } catch (error) {
      console.error('Error counting unlabeled images:', error);
      setVersionUnlabeled(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseBuild = useCallback(() => {
    setIsBuildOpen(false);
    setIsStartingBuild(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartBuild = useCallback(async () => {
    const ownerId = resolveOwnerId();
    if (!ownerId) return;

    // Labeling a version in place: no dataset, no destination to choose.
    if (versionTarget) {
      const payload = {
        owner_id: ownerId,
        task: buildTask,
        source_kind: 'version',
        model_data_info_id: versionTarget.modelDataInfoId,
      };
      if (buildTask === 'classification') {
        if (buildClassMode === 'fixed') payload.num_classes = buildClassCount;
        if ((buildClassNames || '').trim()) payload.class_names = buildClassNames.trim();
      }
      if (buildTask === 'bounding_box') {
        if (!(buildObjects || '').trim()) {
          swalFail(t('videoToImages.build.failed'), t('videoToImages.build.objectsRequired'));
          return;
        }
        payload.vocabulary = buildObjects.trim();
        payload.confidence = buildConfidence;
      }
      setIsStartingBuild(true);
      try {
        await startDatasetBuildAPI(payload);
        setIsBuildOpen(false);
        setVersionTarget(null);
        await handleGetBuilds();
        swalSuccess(t('videoToImages.build.heading'), t('videoToImages.build.queued'));
      } catch (error) {
        console.error('Error starting the in-place build:', error);
        const detail = error?.response?.data?.detail || error?.message;
        swalFail(t('videoToImages.build.failed'), detail || '');
      } finally {
        setIsStartingBuild(false);
      }
      return;
    }

    if (!activeDataset?._id) return;

    const wantsNewProject = creatingProject && Boolean((newProjectName || '').trim());
    const wantsNewModel = creatingModel && Boolean((newModelName || '').trim());
    if (!wantsNewProject && !transferProjectId) {
      swalFail(t('videoToImages.build.failed'), t('videoToImages.transfer.selectModelFirst'));
      return;
    }
    if (!wantsNewModel && !transferModelId) {
      swalFail(t('videoToImages.build.failed'), t('videoToImages.transfer.selectModelFirst'));
      return;
    }
    if (buildTask === 'bounding_box' && !(buildObjects || '').trim()) {
      swalFail(t('videoToImages.build.failed'), t('videoToImages.build.objectsRequired'));
      return;
    }

    const createNew = transferVersionId === 'new' || !transferVersionId;
    const payload = {
      owner_id: ownerId,
      dataset_id: activeDataset._id,
      item_ids: JSON.stringify(Array.from(selectedItemIds)),
      task: buildTask,
    };
    if (wantsNewProject) payload.project_name = newProjectName.trim();
    else payload.project_id = transferProjectId;
    if (wantsNewModel) {
      payload.model_name = newModelName.trim();
      // Only consulted for an `unlabeled` build; an AI task names its own kind.
      payload.model_type = buildTask === 'unlabeled' ? newModelType : buildTask;
    } else {
      payload.model_id = transferModelId;
      // A brand-new model has no version to append to.
      payload.create_new_version = createNew;
      if (!createNew) payload.model_data_info_id = transferVersionId;
    }
    if (buildTask === 'classification') {
      if (buildClassMode === 'fixed') payload.num_classes = buildClassCount;
      if ((buildClassNames || '').trim()) payload.class_names = buildClassNames.trim();
    }
    if (buildTask === 'bounding_box') {
      payload.vocabulary = buildObjects.trim();
      payload.confidence = buildConfidence;
    }

    setIsStartingBuild(true);
    try {
      await startDatasetBuildAPI(payload);
      setIsBuildOpen(false);
      clearItemSelection();
      await handleGetBuilds(activeDataset._id);
      swalSuccess(t('videoToImages.build.heading'), t('videoToImages.build.queued'));
    } catch (error) {
      console.error('Error starting the dataset build:', error);
      const detail = error?.response?.data?.detail || error?.message;
      swalFail(t('videoToImages.build.failed'), detail || '');
    } finally {
      setIsStartingBuild(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionTarget, activeDataset, selectedItemIds, buildTask, buildClassMode, buildClassCount,
      buildClassNames, buildObjects, buildConfidence, creatingProject, newProjectName,
      creatingModel, newModelName, newModelType, transferProjectId, transferModelId,
      transferVersionId, handleGetBuilds, t]);

  const handleCancelBuild = useCallback((buildId) => {
    swalConfirm(
      t('videoToImages.build.cancelConfirmTitle'),
      t('videoToImages.build.cancelConfirmText'),
      async (result) => {
        if (!result?.isConfirmed) return;
        try {
          await cancelDatasetBuildAPI({ build_id: buildId });
          await handleGetBuilds(activeDataset?._id);
          swalSuccess(t('videoToImages.build.heading'), t('videoToImages.build.cancelled'));
        } catch (error) {
          console.error('Error cancelling the build:', error);
          swalFail(t('videoToImages.build.heading'), t('videoToImages.jobs.cancelFailed'));
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDataset, handleGetBuilds, t]);

  /** Open what a finished build produced, in the right labeling studio. */
  const handleOpenBuildResult = useCallback((build) => {
    const result = build?.result;
    if (!result) return;
    const destination = LABELING_TARGET[String(result.modelType || '').toLowerCase()];
    if (!destination || !result.modelDataInfoId) return;
    navigate(destination, {
      state: {
        versionId: result.modelDataInfoId,
        model: {
          // Both spellings: the studios read a mix of the two shapes.
          _id: result.modelId,
          model_id: result.modelId,
          name: result.modelName,
          model_name: result.modelName,
          modelType: result.modelType,
          model_type: result.modelType,
          projectId: result.projectId,
          project_id: result.projectId,
        },
      },
    });
  }, [navigate]);

  // ======================================================================
  // TRANSFER INTO A PROJECT MODEL
  // ======================================================================

  const handleTransferProjectChange = useCallback(async (projectId) => {
    setTransferProjectId(projectId);
    if (!projectId) {
      setTransferModels([]);
      return;
    }
    try {
      const ownerId = resolveOwnerId();
      if (!ownerId) return;
      const response = await getModelsAPI({ owner_id: ownerId, project_id: projectId });
      setTransferModels(
        (Array.isArray(response?.data) ? response.data : []).flatMap((p) => p?.models || [])
      );
    } catch (error) {
      console.error('Error fetching models for transfer:', error);
      setTransferModels([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Load the target model's versions. The default selection is its most recent
   * version, so a transfer ADDS to the version the user is already working in
   * rather than starting a fresh one.
   */
  const handleTransferModelChange = useCallback(async (modelId) => {
    setTransferModelId(modelId);
    setModelVersions([]);
    if (!modelId) return;
    const model = transferModels.find((m) => m.model_id === modelId);
    if (!model) return;
    try {
      const response = await getModelVersionsAPI({
        model_id: modelId, model_type: model.model_type,
      });
      const versions = Array.isArray(response?.data?.data) ? response.data.data : [];
      setModelVersions(versions);
      setTransferVersionId(versions.length ? versions[0]._id : 'new');
    } catch (error) {
      console.error('Error fetching model versions:', error);
      setTransferVersionId('new');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferModels]);

  // ======================================================================
  // START CONVERSION
  // ======================================================================

  const validateBeforeStart = useCallback(() => {
    if (targetMode === 'dataset') {
      if (!datasetName.trim()) return t('videoToImages.target.datasetMissing');
    } else if (!selectedProjectId || !selectedModelId) {
      return t('videoToImages.target.missing');
    }
    if (!selectedFiles.length) return t('videoToImages.upload.noneSelected');
    if (options.samplingMode === 'fps' && !(Number(options.fps) > 0)) return t('videoToImages.options.fpsLabel');
    if (options.samplingMode === 'nth' && !(Number(options.nthFrame) >= 1)) return t('videoToImages.options.nthLabel');
    if (options.samplingMode === 'count' && !(Number(options.totalCount) >= 1)) return t('videoToImages.options.countLabel');
    return null;
  }, [targetMode, datasetName, selectedProjectId, selectedModelId, selectedFiles, options, t]);

  const handleStartConversion = useCallback(async () => {
    const problem = validateBeforeStart();
    if (problem) {
      swalFail(t('videoToImages.start.failed'), problem);
      return;
    }

    const ownerId = resolveOwnerId();
    if (!ownerId) return;

    const isDatasetTarget = targetMode === 'dataset';
    const model = isDatasetTarget ? null : models.find((m) => m.model_id === selectedModelId);
    if (!isDatasetTarget && !model) {
      swalFail(t('videoToImages.start.failed'), t('videoToImages.target.missing'));
      return;
    }

    setIsStarting(true);
    try {
      // --- 1. upload the sources (progress comes from axios) ---
      const totalBytes = selectedFiles.reduce((sum, entry) => sum + (entry.file.size || 0), 0);
      const formData = new FormData();
      formData.append('owner_id', ownerId);
      selectedFiles.forEach((entry) => {
        // Third arg becomes the filename, carrying the folder path with it.
        formData.append('files', entry.file, entry.relativePath);
      });

      setUploadState(true, 0, 0);
      const uploadResponse = await uploadVideoSourcesAPI(formData, (event) => {
        const loaded = event.loaded || 0;
        const total = event.total || totalBytes || 1;
        setUploadState(true, Math.min(100, Math.round((loaded / total) * 100)), loaded);
      });
      setUploadState(false, 100, totalBytes);

      const staged = uploadResponse?.data?.data;
      if (!staged?.sources?.length) {
        throw new Error(t('videoToImages.upload.noneSelected'));
      }
      setStagedBatch(staged);

      // --- 2. queue the background job ---
      const payload = {
        owner_id: ownerId,
        staging_prefix: staged.stagingPrefix,
        sources: JSON.stringify(staged.sources),
        rejected: JSON.stringify(staged.rejected || []),
        sampling_mode: options.samplingMode,
        output_format: options.outputFormat,
        quality: options.quality,
        resize_mode: options.resizeMode,
      };
      if (options.samplingMode === 'fps') payload.fps = options.fps;
      if (options.samplingMode === 'nth') payload.nth_frame = options.nthFrame;
      if (options.samplingMode === 'count') payload.total_count = options.totalCount;
      if (options.resizeMode === 'max') {
        payload.max_width = options.maxWidth;
        payload.max_height = options.maxHeight;
      }
      // Exactly one destination — the API rejects both or neither.
      if (isDatasetTarget) {
        payload.dataset_name = datasetName.trim();
      } else {
        payload.project_id = selectedProjectId;
        payload.model_id = selectedModelId;
        payload.model_type = model.model_type;
      }

      await startExtractionAPI(payload);

      clearSelectedFiles();
      setStagedBatch(null);
      await handleGetJobs();
      if (isDatasetTarget) await handleGetDatasets();
      swalSuccess(t('videoToImages.title'), t('videoToImages.start.queued'));
    } catch (error) {
      console.error('Error starting video conversion:', error);
      const detail = error?.response?.data?.detail || error?.message;
      swalFail(t('videoToImages.start.failed'), detail || t('videoToImages.upload.uploadFailed'));
    } finally {
      setUploadState(false, 0, 0);
      setIsStarting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateBeforeStart, targetMode, datasetName, models, selectedModelId, selectedProjectId, selectedFiles, options, t]);

  const handleCancelJob = useCallback((jobId) => {
    swalConfirm(
      t('videoToImages.jobs.cancelConfirmTitle'),
      t('videoToImages.jobs.cancelConfirmText'),
      async (result) => {
        if (!result?.isConfirmed) return;
        try {
          await cancelVideoJobAPI({ job_id: jobId });
          await handleGetJobs();
          swalSuccess(t('videoToImages.title'), t('videoToImages.jobs.cancelled'));
        } catch (error) {
          console.error('Error cancelling video job:', error);
          swalFail(t('videoToImages.title'), t('videoToImages.jobs.cancelFailed'));
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleGetJobs, t]);

  const handleOpenDetails = useCallback(async (jobId) => {
    try {
      const [jobResponse, logsResponse] = await Promise.all([
        getVideoJobAPI({ job_id: jobId }),
        getVideoJobLogsAPI({ job_id: jobId, limit: 200 }).catch(() => null),
      ]);
      setDetailJob(jobResponse?.data?.data || null);
      setDetailLogs(logsResponse?.data?.data?.logs || []);
    } catch (error) {
      console.error('Error fetching job details:', error);
      swalFail(t('videoToImages.details.heading'), t('videoToImages.jobs.loadFailed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDetailJob, setDetailLogs, t]);

  const handleCloseDetails = useCallback(() => {
    setDetailJob(null);
    setDetailLogs([]);
  }, [setDetailJob, setDetailLogs]);

  /**
   * Open a finished job's output: the labeling tool for a model target, or the
   * dataset browser for a dataset target.
   */
  const handleOpenTarget = useCallback((job) => {
    if (job?.targetKind === 'dataset') {
      if (job?.datasetId) handleOpenDataset(job.datasetId);
      return;
    }
    const destination = LABELING_TARGET[String(job?.modelType || '').toLowerCase()];
    if (!destination || !job?.modelDataInfoId) return;
    navigate(destination, {
      state: {
        versionId: job.modelDataInfoId,
        model: {
          model_id: job.modelId,
          model_name: job.modelName,
          model_type: job.modelType,
          project_id: job.projectId,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, handleOpenDataset]);

  const canOpenTarget = useCallback((job) => {
    if (Number(job?.totals?.framesExtracted || 0) <= 0) return false;
    if (job?.targetKind === 'dataset') return Boolean(job?.datasetId);
    return Boolean(
      job?.modelDataInfoId && LABELING_TARGET[String(job?.modelType || '').toLowerCase()]
    );
  }, []);

  return {
    // state
    engine, supportedExtensions,
    targetMode, datasetName,
    projects, models, selectedProjectId, selectedModelId,
    selectedFiles, dragOver,
    isUploading, uploadPercent, uploadedBytes,
    stagedBatch, options, jobs, jobsError, isStarting,
    detailJob, detailLogs,
    datasets, datasetsError, activeDataset, datasetItems, datasetTotal,
    isDatasetLoading, selectedItemIds,
    creatingProject, newProjectName, creatingModel, newModelName, newModelType,
    isCreatingTarget,
    isDownloading,
    isBuildOpen, versionTarget, versionUnlabeled,
    buildTask, buildClassMode, buildClassCount, buildClassNames,
    buildObjects, buildConfidence, isStartingBuild, builds,
    transferProjectId, transferModelId, transferVersionId,
    transferModels, modelVersions,
    // handlers
    handlers: {
      handleGetEngineInfo,
      handleGetProjects,
      handleGetModels,
      handleSelectProject,
      setSelectedModelId,
      setTargetMode,
      setDatasetName,
      handleFilesPicked,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handleRemoveFile,
      handleClearFiles,
      setOption,
      resetOptions,
      handleStartConversion,
      handleGetJobs,
      handleCancelJob,
      handleOpenDetails,
      handleCloseDetails,
      handleOpenTarget,
      canOpenTarget,
      // datasets
      handleGetDatasets,
      handleOpenDataset,
      handleLoadMoreItems,
      handleCloseDataset,
      toggleItemSelection,
      handleSelectAllItems,
      clearItemSelection,
      handleRemoveSelected,
      handleDeleteDataset,
      handleDownloadDataset,
      // creating a destination inline
      setCreatingProject,
      setNewProjectName,
      setCreatingModel,
      setNewModelName,
      setNewModelType,
      handleCreateProject,
      handleCreateModel,
      // preparing for labeling
      setBuildTask,
      setBuildClassMode,
      setBuildClassCount,
      setBuildClassNames,
      setBuildObjects,
      setBuildConfidence,
      handleOpenBuild,
      handleOpenVersionBuild,
      handleCloseBuild,
      handleStartBuild,
      handleGetBuilds,
      handleCancelBuild,
      handleOpenBuildResult,
      handleTransferProjectChange,
      handleTransferModelChange,
      setTransferVersionId,
      resetAll,
    },
  };
};

export default useVideoToImagesHook;
